import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Plus, User, Reply, ThumbsUp, Smile, Heart, Check } from 'lucide-react';
import { useAuth } from '../services/AuthContext';
import { formatSecondsToTime } from '../utils/timestampParser';
import ReactMarkdown from 'react-markdown';

interface Comment {
  id: string;
  userEmail: string;
  userName: string;
  content: string;
  timestampSeconds?: number;
  parentCommentId?: string;
  replies?: Comment[];
  reactions?: CommentReaction[];
  timestamp: Date;
  displayName?: string;
  avatarColor?: string;
}

interface CommentReaction {
  emoji: string;
  count: number;
  userReacted: boolean;
}

interface TimelineCommentsProps {
  comments: Comment[];
  duration: number;
  currentTime: number;
  viewportStart?: number;
  viewportEnd?: number;
  timeToX?: (time: number) => number;
  onAddComment: (content: string, timestampSeconds: number) => Promise<void>;
  onDeleteComment?: (commentId: string) => Promise<void>;
  onReply?: (parentCommentId: string, content: string) => Promise<void>;
  onReact?: (commentId: string, emoji: string) => Promise<void>;
  onSelectComment?: (commentId: string) => void;
  selectedCommentId?: string | null;
  isDarkMode: boolean;
  width: number;
  height?: number;
  onHeightChange?: (height: number) => void;
}

const AVATAR_SIZE = 24;
const TIMELINE_HEIGHT = 60;
const MIN_TIMELINE_HEIGHT = 40;
const MAX_TIMELINE_HEIGHT = 120;
const MIN_SPACING = 30; // Minimum spacing between avatars
const clampHeight = (value: number) => Math.min(Math.max(value, MIN_TIMELINE_HEIGHT), MAX_TIMELINE_HEIGHT);
const clampWidth = (value: number, width: number) => Math.min(Math.max(value, 0), width);

const TimelineComments: React.FC<TimelineCommentsProps> = ({
  comments,
  duration,
  currentTime,
  viewportStart,
  viewportEnd,
  timeToX,
  onAddComment,
  onDeleteComment,
  onReply,
  onReact,
  onSelectComment,
  selectedCommentId,
  isDarkMode,
  width,
  height = TIMELINE_HEIGHT,
  onHeightChange
}) => {
  const { user } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showAddCommentModal, setShowAddCommentModal] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [openClusterId, setOpenClusterId] = useState<string | null>(null);

  const resolveX = useCallback((time: number) => {
    if (timeToX) {
      return timeToX(time);
    }
    if (duration <= 0 || width <= 0) {
      return 0;
    }
    return (time / duration) * width;
  }, [duration, timeToX, width]);

  // Get user initials
  const getUserInitials = (nameOrEmail: string) => {
    if (!nameOrEmail || nameOrEmail === 'Anonymous') return 'AN';
    // If it's an email, extract the part before @
    if (nameOrEmail.includes('@')) {
      return nameOrEmail.split('@')[0].substring(0, 2).toUpperCase();
    }
    // If it's a name, use first two letters or initials
    const words = nameOrEmail.trim().split(' ');
    if (words.length > 1) {
      // Use first letter of first and last name
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    // Single word name - use first two letters
    return nameOrEmail.substring(0, 2).toUpperCase();
  };

  // Get user color
  const getUserColor = (email: string) => {
    const colors = [
      'bg-[#2f3e52]', 'bg-[#445872]', 'bg-[#8d6723]', 'bg-[#784735]',
      'bg-[#5f6d4d]', 'bg-[#7b4a3b]', 'bg-[#5a5446]', 'bg-[#9b7726]'
    ];
    const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // Count all descendants (replies and their replies, etc.)
  const countAllDescendants = (comment: Comment): number => {
    if (!comment.replies || comment.replies.length === 0) {
      return 0;
    }
    
    let count = comment.replies.length;
    comment.replies.forEach(reply => {
      count += countAllDescendants(reply);
    });
    
    return count;
  };

  // Build comment tree from flat array
  const commentsWithReplies = useMemo(() => {
    const commentMap = new Map<string, Comment>();
    const rootComments: Comment[] = [];
    
    // First pass: create map of all comments
    comments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, replies: [] });
    });
    
    // Second pass: build tree structure
    comments.forEach(comment => {
      if (comment.parentCommentId) {
        const parent = commentMap.get(comment.parentCommentId);
        if (parent && parent.replies) {
          parent.replies.push(commentMap.get(comment.id)!);
        }
      } else {
        rootComments.push(commentMap.get(comment.id)!);
      }
    });
    
    return rootComments;
  }, [comments]);

  // Filter comments that have timestamps
  const timelineComments = useMemo(() => {
    return commentsWithReplies.filter(comment => {
      const timestamp = comment.timestampSeconds;
      if (timestamp === null || timestamp === undefined) {
        return false;
      }

      if (viewportStart === undefined || viewportEnd === undefined) {
        return true;
      }

      return timestamp >= viewportStart && timestamp <= viewportEnd;
    });
  }, [commentsWithReplies, viewportEnd, viewportStart]);

  const commentClusters = useMemo(() => {
    const sorted = [...timelineComments].sort((a, b) => (a.timestampSeconds || 0) - (b.timestampSeconds || 0));
    const clusters: Array<{
      id: string;
      comments: Comment[];
      x: number;
      lastX: number;
      startTime: number;
      endTime: number;
    }> = [];

    sorted.forEach((comment) => {
      const x = resolveX(comment.timestampSeconds || 0);
      if (x < -AVATAR_SIZE || x > width + AVATAR_SIZE) {
        return;
      }

      const lastCluster = clusters[clusters.length - 1];
      if (lastCluster && x - lastCluster.lastX < MIN_SPACING) {
        const nextCount = lastCluster.comments.length + 1;
        lastCluster.comments.push(comment);
        lastCluster.startTime = Math.min(lastCluster.startTime, comment.timestampSeconds || 0);
        lastCluster.endTime = Math.max(lastCluster.endTime, comment.timestampSeconds || 0);
        lastCluster.x = ((lastCluster.x * (nextCount - 1)) + x) / nextCount;
        lastCluster.lastX = x;
        return;
      }

      clusters.push({
        id: comment.id,
        comments: [comment],
        x,
        lastX: x,
        startTime: comment.timestampSeconds || 0,
        endTime: comment.timestampSeconds || 0,
      });
    });

    return clusters.map((cluster) => ({
      ...cluster,
      id: cluster.comments.length > 1
        ? `cluster-${cluster.comments.map((comment) => comment.id).join('-')}`
        : cluster.id,
    }));
  }, [resolveX, timelineComments, width]);

  const computedHeight = useMemo(() => {
    return clampHeight(TIMELINE_HEIGHT);
  }, []);

  useEffect(() => {
    onHeightChange?.(computedHeight);
  }, [computedHeight, onHeightChange]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpenClusterId(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const addButtonX = useMemo(() => {
    return clampWidth(resolveX(currentTime), width);
  }, [currentTime, resolveX, width]);

  // Handle adding comment at current playhead position
  const handleAddCommentAtPlayhead = useCallback(() => {
    setShowAddCommentModal(true);
    setNewCommentText(`${formatSecondsToTime(currentTime)} - `);
  }, [currentTime]);

  // Submit new comment
  const handleSubmitComment = useCallback(async () => {
    if (!newCommentText.trim() || isAddingComment) return;
    
    setIsAddingComment(true);
    try {
      await onAddComment(newCommentText, currentTime);
      setNewCommentText('');
      setShowAddCommentModal(false);
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setIsAddingComment(false);
    }
  }, [newCommentText, currentTime, onAddComment, isAddingComment]);

  const getClusterAlignment = (x: number) => {
    if (x < 140) {
      return { containerClassName: 'left-0', arrowClassName: 'left-5' };
    }
    if (x > width - 140) {
      return { containerClassName: 'right-0', arrowClassName: 'right-5' };
    }
    return { containerClassName: 'left-1/2 -translate-x-1/2', arrowClassName: 'left-1/2 -translate-x-1/2' };
  };

  const getCommentSnippet = (comment: Comment) => {
    const trimmed = String(comment.content || '').replace(/\s+/g, ' ').trim();
    return trimmed.length > 96 ? `${trimmed.slice(0, 96)}...` : trimmed;
  };

  return (
    <div
      ref={containerRef}
      className={`relative rounded-lg overflow-visible ${
        isDarkMode ? 'bg-[#1d2636]' : 'bg-[#ecedf0]'
      }`}
      style={{ width, height: computedHeight }}
    >
      
      {/* Timeline background */}
      <div className={`absolute inset-0 opacity-60 ${
        isDarkMode ? 'bg-[#253146]' : 'bg-white/70'
      }`} />
      
      {/* Add Comment Button - follows playhead */}
      {onAddComment && (
        <motion.button
          className="absolute z-30 rounded-full p-1.5 shadow-lg transition-colors bg-[#FFB52B] hover:bg-[#f0a61d] text-[#232E3F]"
          style={{ 
            left: `${addButtonX - 12}px`,
            top: `${computedHeight / 2 - 12}px`
          }}
          onClick={() => onAddComment('', currentTime)}
          title={`Add comment at ${formatSecondsToTime(currentTime)}`}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <Plus size={16} />
        </motion.button>
      )}
      
      {/* Comment avatars */}
      <AnimatePresence>
        {commentClusters.map((cluster) => {
          const isMulti = cluster.comments.length > 1;
          const isOpen = openClusterId === cluster.id;
          const isSelected = cluster.comments.some((comment) => comment.id === selectedCommentId);
          const alignment = getClusterAlignment(cluster.x);

          if (!isMulti) {
            const comment = cluster.comments[0];
            return (
              <motion.div
                key={cluster.id}
                data-timeline-pan-ignore="true"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                whileHover={{ scale: 1.1 }}
                className="absolute cursor-pointer z-20 group"
                style={{
                  left: `${cluster.x - AVATAR_SIZE / 2}px`,
                  top: `${computedHeight / 2 - AVATAR_SIZE / 2}px`
                }}
                onClick={() => onSelectComment?.(comment.id)}
              >
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white ${comment.avatarColor ? '' : getUserColor(comment.displayName || comment.userName || comment.userEmail)} shadow-lg ${isSelected ? 'ring-2 ring-[#ffcf74]' : ''}`}
                  style={comment.avatarColor ? { backgroundColor: comment.avatarColor } : {}}
                >
                  {comment.displayName || comment.userName ? (
                    getUserInitials(comment.displayName || comment.userName)
                  ) : comment.userEmail.includes('@') && comment.userEmail !== 'Anonymous' ? (
                    getUserInitials(comment.userEmail)
                  ) : (
                    <User size={14} />
                  )}
                </div>

                {comment.replies && comment.replies.length > 0 && (
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#232E3F] text-[#FFCF74] rounded-full flex items-center justify-center text-[10px] font-semibold shadow-sm border border-white/80">
                    {countAllDescendants(comment)}
                  </div>
                )}

                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20">
                  <div className="rounded-lg px-3 py-2 text-sm whitespace-normal shadow-lg border border-white/10 bg-[#232E3F] text-[#F4F4F4]" style={{ minWidth: '300px', maxWidth: '450px' }}>
                    <div className="font-semibold text-xs mb-1">
                      {comment.displayName || comment.userName || comment.userEmail}
                    </div>
                    <div className="text-xs">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <p className="mb-0">{children}</p>,
                          strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                          em: ({ children }) => <em className="italic">{children}</em>,
                          code: ({ children }) => <code className="bg-black/20 px-1 rounded text-xs text-[#ffe2a6]">{children}</code>,
                        }}
                      >
                        {comment.content.length > 100 ? `${comment.content.slice(0, 100)}...` : comment.content}
                      </ReactMarkdown>
                    </div>
                    <div className="text-xs mt-1 opacity-70">
                      {formatSecondsToTime(comment.timestampSeconds || 0)}
                    </div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
                      <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px]"
                        style={{ borderTopColor: '#232E3F' }} />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          }

          return (
            <motion.div
              key={cluster.id}
              data-timeline-pan-ignore="true"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              whileHover={{ scale: 1.04 }}
              className="absolute z-20"
              style={{
                left: `${cluster.x - 18}px`,
                top: `${computedHeight / 2 - 12}px`
              }}
            >
              <button
                type="button"
                onClick={() => setOpenClusterId((current) => current === cluster.id ? null : cluster.id)}
                className={`flex h-6 min-w-[36px] items-center justify-center gap-1 rounded-full px-2 text-[11px] font-semibold shadow-lg transition-colors bg-[#FFB52B] text-[#232E3F] hover:bg-[#f0a61d] ${
                  isSelected ? 'ring-2 ring-[#ffcf74]' : ''
                }`}
                title={`${cluster.comments.length} nearby comments`}
              >
                <MessageCircle size={12} />
                <span>{cluster.comments.length}</span>
              </button>

              {isOpen && (
                <div className={`absolute bottom-full mb-3 w-80 ${alignment.containerClassName}`}>
                  <div className="rounded-xl border shadow-2xl border-white/10 bg-[#232E3F] text-[#F4F4F4]">
                    <div className="border-b border-white/10 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#ffcf74]">
                      {cluster.comments.length} comments nearby
                    </div>
                    <div className="max-h-72 overflow-y-auto p-2">
                      {cluster.comments.map((comment) => {
                        const replyCount = countAllDescendants(comment);
                        return (
                          <button
                            key={comment.id}
                            type="button"
                            onClick={() => {
                              onSelectComment?.(comment.id);
                              setOpenClusterId(null);
                            }}
                            className={`mb-2 w-full rounded-lg border px-3 py-2 text-left transition-colors last:mb-0 ${
                              selectedCommentId === comment.id
                                ? 'border-[#ffcf74] bg-white/10'
                                : 'border-white/10 hover:bg-white/5'
                            }`}
                          >
                            <div className="mb-1 flex items-center justify-between gap-3">
                              <span className="truncate text-xs font-semibold">
                                {comment.displayName || comment.userName || comment.userEmail}
                              </span>
                              <span className="shrink-0 text-[11px] text-[#c3ccd9]">
                                {formatSecondsToTime(comment.timestampSeconds || 0)}
                              </span>
                            </div>
                            <div className="text-xs leading-5 text-[#edf1f7]">
                              {getCommentSnippet(comment) || 'Open comment'}
                            </div>
                            {replyCount > 0 && (
                              <div className="mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-white/10 text-[#ffcf74]">
                                <Reply size={10} />
                                <span>{replyCount} repl{replyCount === 1 ? 'y' : 'ies'}</span>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className={`absolute top-full -mt-px ${alignment.arrowClassName}`}>
                    <div
                      className="h-0 w-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent"
                      style={{ borderTopColor: '#232E3F' }}
                    />
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
      
      
      {/* Removed modal - using main comments panel instead */}
      <AnimatePresence>
        {false && showAddCommentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={() => setShowAddCommentModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`${isDarkMode ? 'bg-[#1d2636]' : 'bg-white'} rounded-lg shadow-xl p-6 max-w-md w-full mx-4`}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Add Comment at {formatSecondsToTime(currentTime)}
              </h3>
              <textarea
                value={newCommentText}
                onChange={(e) => setNewCommentText(e.target.value)}
                placeholder="Write your comment..."
                className={`w-full p-3 border rounded-lg resize-none ${
                  isDarkMode 
                    ? 'bg-[#131a25] border-[#40506a] text-white' 
                    : 'bg-white border-[#c9d1dc] text-gray-900'
                } focus:ring-2 focus:ring-[#ffcf74] focus:border-transparent`}
                rows={4}
                autoFocus
              />
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setShowAddCommentModal(false)}
                  className={`px-4 py-2 rounded-lg ${
                    isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                  } transition-colors`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitComment}
                  disabled={!newCommentText.trim() || isAddingComment}
                  className="px-4 py-2 bg-[#FFB52B] hover:bg-[#f0a61d] text-[#232E3F] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isAddingComment ? 'Adding...' : 'Add Comment'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TimelineComments;
